/**
 * Access control (ACL) admin — grant / revoke capabilities per user in the
 * current org. Gated on `acl:manage` (§ACL). This screen is UX only; the real
 * gate is Oxbo-M, which re-resolves the ACTING users `acl:manage` on every
 * grant/revoke and rejects anyone else (defense-in-depth, §22c / §8).
 *
 * Why a user-id input (not a member list)? Oxbo-M exposes NO SPA-callable
 * user-list endpoint and NO JWT-bearing `GET /users/{id}/capabilities` (its
 * per-user resolver is the service-token-only `/internal/...` route, which the
 * SPA must not hold the secret for). So the manager enters the target users
 * id; the grant/revoke response returns the resulting capability set, which we
 * display as that users current capabilities. See the task report for the
 * flagged M-endpoint gap.
 *
 * Minimal but functional: pick the target (user id), tick the capabilities,
 * Grant or Revoke. Server errors — the 409 "last ACL manager" and the
 * non-member rejection — are surfaced in Spanish.
 */
import { useMemo, useState } from "react";
import { useLocale } from "@hooks/useLocale";
import { useCapabilities } from "@components/catalog/useCapabilities";
import { CAPABILITIES } from "@api/capabilities";
import {
  useGetCapabilityDictQuery,
  useGrantCapabilitiesMutation,
  useRevokeCapabilitiesMutation,
} from "@api/mApi";
import { getProdOrgId, isProdAuthEnabled } from "@api/prodAuth";
import { QueryState } from "@components/ui/QueryState";
import type { CapabilitiesResponse } from "@api/types";
import "./accessControl.css";

/** Fallback org id (MSW/dev) when prod-auth is off — matches the mock seed. */
const DEMO_ORG_ID = "00000000-0000-0000-0000-0000000000a0";

/** Extract a Spanish error message from an RTK-query error, if present. */
function errorDetail(err: unknown): string | null {
  if (err && typeof err === "object" && "data" in err) {
    const data = (err as { data?: unknown }).data;
    if (data && typeof data === "object" && "detail" in data) {
      const detail = (data as { detail?: unknown }).detail;
      if (typeof detail === "string") return detail;
    }
  }
  return null;
}

export function AccessControl() {
  const { t } = useLocale();
  const { can } = useCapabilities();

  // Self-gate (belt-and-suspenders to the nav + router gate). The server is the
  // real gate on grant/revoke regardless.
  if (!can(CAPABILITIES.aclManage)) {
    return (
      <div>
        <div className="page-head">
          <h1>{t("acl.title")}</h1>
          <p className="subtitle">{t("acl.subtitle")}</p>
        </div>
        <div className="card">
          <div className="empty-state">{t("acl.managerOnly")}</div>
        </div>
      </div>
    );
  }

  return <AccessControlInner />;
}

function AccessControlInner() {
  const { t } = useLocale();
  const orgId = (isProdAuthEnabled() ? getProdOrgId() : null) ?? DEMO_ORG_ID;

  const { data: dict = [], isLoading, isError, refetch } = useGetCapabilityDictQuery();
  const [grant, grantState] = useGrantCapabilitiesMutation();
  const [revoke, revokeState] = useRevokeCapabilitiesMutation();

  const [userId, setUserId] = useState("");
  const [reason, setReason] = useState("");
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [result, setResult] = useState<CapabilitiesResponse | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const busy = grantState.isLoading || revokeState.isLoading;
  const selectedCaps = useMemo(
    () => Object.entries(selected).filter(([, v]) => v).map(([k]) => k),
    [selected],
  );
  const userIdValid = userId.trim().length > 0;
  const canSubmit = userIdValid && selectedCaps.length > 0 && !busy;

  const toggle = (key: string) =>
    setSelected((s) => ({ ...s, [key]: !s[key] }));

  const run = async (op: "grant" | "revoke") => {
    setErrorMsg(null);
    setResult(null);
    const body = {
      organization_id: orgId,
      capabilities: selectedCaps,
      ...(reason.trim() ? { change_reason: reason.trim() } : {}),
    };
    const action = op === "grant" ? grant : revoke;
    const res = await action({ userId: userId.trim(), body });
    if ("data" in res && res.data) {
      setResult(res.data);
    } else if ("error" in res) {
      setErrorMsg(errorDetail(res.error) ?? t("acl.error.generic"));
    }
  };

  return (
    <div>
      <div className="page-head">
        <h1>{t("acl.title")}</h1>
        <p className="subtitle">{t("acl.subtitle")}</p>
      </div>

      <div className="card acl-note" role="note">
        <span aria-hidden="true">🔑</span> {t("acl.note")}
      </div>

      <QueryState
        isLoading={isLoading}
        isError={isError}
        isEmpty={dict.length === 0}
        emptyMessage={t("acl.noDict")}
        onRetry={refetch}
      >
        <div className="card acl-form">
          <div className="field">
            <label htmlFor="acl-user">{t("acl.userId")}</label>
            <input
              id="acl-user"
              type="text"
              inputMode="text"
              autoComplete="off"
              placeholder={t("acl.userId.ph")}
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
            />
            <p className="settings-hint">{t("acl.userId.hint")}</p>
          </div>

          <fieldset className="acl-caps">
            <legend>{t("acl.capabilities")}</legend>
            {dict.map((c) => (
              <label key={c.capability_key} className="acl-cap-row">
                <input
                  type="checkbox"
                  checked={!!selected[c.capability_key]}
                  onChange={() => toggle(c.capability_key)}
                />
                <span className="mono">{c.capability_key}</span>
                <span className="dim">{c.display_label}</span>
              </label>
            ))}
          </fieldset>

          <div className="field">
            <label htmlFor="acl-reason">{t("acl.reason")}</label>
            <input
              id="acl-reason"
              type="text"
              autoComplete="off"
              placeholder={t("acl.reason.ph")}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>

          <div className="acl-actions">
            <button
              type="button"
              className="btn primary"
              disabled={!canSubmit}
              onClick={() => void run("grant")}
            >
              {grantState.isLoading ? t("acl.granting") : t("acl.grant")}
            </button>
            <button
              type="button"
              className="btn"
              disabled={!canSubmit}
              onClick={() => void run("revoke")}
            >
              {revokeState.isLoading ? t("acl.revoking") : t("acl.revoke")}
            </button>
          </div>

          {errorMsg ? <div className="arch-inline-error">{errorMsg}</div> : null}

          {result ? (
            <div className="acl-result">
              <div className="acl-result-head">
                {t("acl.result.for")} <span className="mono">{result.user_id}</span>
              </div>
              {result.capabilities.length ? (
                <ul className="acl-result-list">
                  {result.capabilities.map((c) => (
                    <li key={c} className="mono">
                      {c}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="dim">{t("acl.result.none")}</div>
              )}
            </div>
          ) : null}
        </div>
      </QueryState>
    </div>
  );
}
